import {computed, defineComponent, onBeforeMount, ref, watch} from 'vue';
import {
    ActorSearchResult,
    ActorSearchResultsDto,
    AthumiService,
    SearchActorsRequest
} from "@/api/enterprise-gateway-api.proxy.ts";
import {debounce} from "lodash-es";
import {useEnterpriseGatewayApiProxy} from "@/plugins/proxy-client-plugin.ts";
import {useFormatting} from "@/composables/currency-format.ts";
import {AdminActors} from "@/app/admin/admin-routes.ts";
import {getAvailableServices} from "@/app/shared/helpers/services.ts";
import {InputText, Select} from 'primevue';

export default defineComponent({
    name: 'AegAdminSearchActor',
    components: { InputText, Select },
    setup: function () {
        const apiProxy = useEnterpriseGatewayApiProxy();
        const {formatCurrency} = useFormatting();

        const identifier = ref<string | undefined>(undefined);
        const name = ref<string | undefined>(undefined);
        const selectedService = ref<AthumiService | undefined>(undefined);
        const isLoading = ref<boolean>(false);
        const emptyState = ref<boolean>(false);
        const searchResults = ref<ActorSearchResultsDto | undefined>(undefined);
        const actors = ref<ActorSearchResult[]>([]);
        const pageSize = ref<number>(50);
        const currentPage = ref<number>(1);

        const serviceOptions = getAvailableServices();

        // Computed for search filters only (excludes pagination)
        const searchFilters = computed(() => ({
            identifier: identifier.value,
            name: name.value,
            services: selectedService.value ? [selectedService.value] : []
        }));

        const performSearch = async () => {
            try {
                isLoading.value = true;
                const request = SearchActorsRequest.fromJS({
                    identifier: identifier.value,
                    name: name.value,
                    services: selectedService.value ? [selectedService.value] : [],
                    page: currentPage.value,
                    pageSize: pageSize.value
                });

                const results = await apiProxy.actor_Search(request);

                if (results) {
                    searchResults.value = results;
                    actors.value = results.actors || [];
                    emptyState.value = actors.value.length === 0;
                } else {
                    searchResults.value = undefined;
                    actors.value = [];
                    emptyState.value = true;
                }
            } catch (error) {
                console.error('Error searching actors:', error);
                searchResults.value = undefined;
                actors.value = [];
                emptyState.value = true;
            } finally {
                isLoading.value = false;
            }
        };

        const debouncedSearch = debounce(async () => {
            currentPage.value = 1;
            await performSearch();
        }, 300);

        const searchFieldChanged = () => {
            debouncedSearch();
        }

        const search = async () => {
            currentPage.value = 1;
            await performSearch();
        };

        const changePage = async (page: number) => {
            currentPage.value = page;
            await performSearch();
        };

        const getServiceBalance = (actor: ActorSearchResult, serviceName: string): string => {
            if (!actor.serviceBalances || actor.serviceBalances.length === 0) {
                return '-';
            }
            const serviceBalance = actor.serviceBalances.find(sb => sb.service === serviceName);
            return serviceBalance && serviceBalance.balance !== undefined
                ? formatCurrency(serviceBalance.balance)
                : '-';
        };

        // Watch only search filters, not pagination
        watch(searchFilters, searchFieldChanged, {immediate: false, deep: true});

        onBeforeMount(async () => {
            await performSearch();
        });

        return {
            identifier,
            name,
            selectedService,
            serviceOptions,
            isLoading,
            emptyState,
            searchResults,
            actors,
            pageSize,
            search,
            changePage,
            formatCurrency,
            getServiceBalance,
            adminActorsRouteName: AdminActors.name
        };
    },
})








